import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import { countries } from '../data/countries';
import './AdvertiserForm.css';

const AddAdvertiser: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [countryError, setCountryError] = useState<string | null>(null);
  const [countrySearch, setCountrySearch] = useState('');
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [filteredCountries, setFilteredCountries] = useState<string[]>(countries);
  const countryInputRef = useRef<HTMLInputElement>(null);
  const countryDropdownRef = useRef<HTMLDivElement>(null);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    companyName: '',
    url: '',
    mobile: '',
    email: '',
    address: '',
    city: '',
    state: '',
    country: '',
    postbackAuthMethod: 'hmac',
    whitelistIp: '',
    monthlyBudget: ''
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCountrySearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const exactCountry = countries.find(country => country.toLocaleLowerCase('en-US') === value.trim().toLocaleLowerCase('en-US'));
    setCountrySearch(value);
    setFormData(prev => ({ ...prev, country: exactCountry || '' }));
    setCountryError(null);
    setShowCountryDropdown(true);

    // Filter countries based on search term
    const filtered = countries.filter(country =>
      country.toLowerCase().includes(value.toLowerCase())
    );
    setFilteredCountries(filtered);
  };

  const handleCountrySelect = (country: string) => {
    setFormData(prev => ({ ...prev, country }));
    setCountrySearch(country);
    setCountryError(null);
    setShowCountryDropdown(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        countryDropdownRef.current &&
        !countryDropdownRef.current.contains(event.target as Node) &&
        countryInputRef.current &&
        !countryInputRef.current.contains(event.target as Node)
      ) {
        setShowCountryDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Sync countrySearch with formData.country on mount
  useEffect(() => {
    if (formData.country) {
      setCountrySearch(formData.country);
    }
  }, [formData.country]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.country) {
      setCountryError('Select a country from the available list');
      countryInputRef.current?.focus();
      return;
    }

    setLoading(true);

    try {
      // Map form data to API format
      const apiData = {
        name: formData.companyName,
        contact_email: formData.email,
        postback_url_template: formData.url,
        postback_auth_method: formData.postbackAuthMethod,
        whitelisted_ips: formData.whitelistIp ? formData.whitelistIp.split(',').map(ip => ip.trim()) : [],
        billing_contact: {
          first_name: formData.firstName,
          last_name: formData.lastName,
          email: formData.email,
          phone: formData.mobile || '',
          address: formData.address || '',
          city: formData.city || '',
          state: formData.state || '',
          country: formData.country || '',
        },
        monthly_budget: formData.monthlyBudget ? parseFloat(formData.monthlyBudget) : undefined,
        budget_alert_threshold: 80,
        status: 'active'
      };

      await apiService.createAdvertiser(apiData);

      // Navigate back to advertisers list on success
      navigate('/advertisers');
    } catch (err: any) {
      setError(err.message || 'Failed to create advertiser');
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/advertisers');
  };

  return (
    <div className="advertiser-form-page">
      <div className="form-header">
        <h1>Add Advertiser</h1>
        <button
          className="btn btn-secondary"
          onClick={handleCancel}
        >
          ← Back to Advertisers
        </button>
      </div>

      <div className="form-container">
        <form onSubmit={handleSubmit} className="advertiser-form">
          <div className="form-grid">
            {/* Left Column */}
            <div className="form-column">
              <div className="form-group">
                <label htmlFor="firstName">First Name*</label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  className="form-input"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="companyName">Company Name*</label>
                <input
                  type="text"
                  id="companyName"
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleInputChange}
                  className="form-input"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="mobile">Mobile</label>
                <input
                  type="tel"
                  id="mobile"
                  name="mobile"
                  value={formData.mobile}
                  onChange={handleInputChange}
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="address">Address*</label>
                <input
                  type="text"
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  className="form-input"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="city">City</label>
                <input
                  type="text"
                  id="city"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  className="form-input"
                />
              </div>

              <div className="form-group" style={{ position: 'relative' }}>
                <label htmlFor="country">Country*</label>
                <input
                  ref={countryInputRef}
                  type="text"
                  id="country"
                  name="country"
                  value={countrySearch}
                  onChange={handleCountrySearch}
                  onFocus={() => setShowCountryDropdown(true)}
                  className="form-input"
                  placeholder="Type to search countries..."
                  required
                  autoComplete="off"
                />
                {showCountryDropdown && filteredCountries.length > 0 && (
                  <div
                    ref={countryDropdownRef}
                    style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      maxHeight: '200px',
                      overflowY: 'auto',
                      backgroundColor: '#4a5568',
                      border: '1px solid #5a6c7d',
                      borderRadius: '4px',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                      zIndex: 1000,
                      marginTop: '4px'
                    }}
                  >
                    {filteredCountries.map((country) => (
                      <div
                        key={country}
                        onClick={() => handleCountrySelect(country)}
                        style={{
                          padding: '8px 12px',
                          cursor: 'pointer',
                          borderBottom: '1px solid #5a6c7d',
                          color: '#ecf0f1',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#3c4855'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                      >
                        {country}
                      </div>
                    ))}
                  </div>
                )}
                {countryError && <span className="error-message" role="alert">{countryError}</span>}
              </div>
            </div>

            {/* Right Column */}
            <div className="form-column">
              <div className="form-group">
                <label htmlFor="lastName">Last Name*</label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  className="form-input"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="url">Advertiser Postback URL Template*</label>
                <input
                  type="url"
                  id="url"
                  name="url"
                  value={formData.url}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="https://advertiser.example/postback?click_id={click_id}"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="email">E-mail*</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="form-input"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="state">State</label>
                <input
                  type="text"
                  id="state"
                  name="state"
                  value={formData.state}
                  onChange={handleInputChange}
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="monthlyBudget">Monthly Budget</label>
                <input
                  type="number"
                  id="monthlyBudget"
                  name="monthlyBudget"
                  value={formData.monthlyBudget}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="0"
                  min="0"
                  step="0.01"
                />
              </div>
            </div>
          </div>

          {/* Full Width Fields */}
          <div className="form-group full-width">
            <label htmlFor="postbackAuthMethod">
              Postback Authentication Method*
              <span style={{ fontSize: '12px', color: '#a0aec0', marginLeft: '10px' }}>
                (How advertiser proves conversions are legitimate)
              </span>
            </label>
            <select
              id="postbackAuthMethod"
              name="postbackAuthMethod"
              value={formData.postbackAuthMethod}
              onChange={handleInputChange}
              className="form-select"
              required
            >
              <option value="hmac">HMAC Signature (Most Secure - Recommended)</option>
              <option value="ip_whitelist">IP Whitelist (Simple - Medium Security)</option>
              <option value="api_key">API Key Only (Testing/Trusted Partners - Low Security)</option>
              <option value="none">No Authentication (Attribution Platforms - No Security)</option>
            </select>
            <div style={{ marginTop: '8px', fontSize: '12px', color: '#a0aec0' }}>
              {formData.postbackAuthMethod === 'hmac' && (
                <span>✅ <strong>HMAC:</strong> Advertiser must generate cryptographic signatures. Best for high-value offers and fraud prevention.</span>
              )}
              {formData.postbackAuthMethod === 'ip_whitelist' && (
                <span>⚠️ <strong>IP Whitelist:</strong> Only accept postbacks from specific IPs. Good for advertisers who can't implement HMAC. Enter IPs below.</span>
              )}
              {formData.postbackAuthMethod === 'api_key' && (
                <span>⚠️ <strong>API Key Only:</strong> Minimal security. Use only for testing or extremely trusted partners.</span>
              )}
              {formData.postbackAuthMethod === 'none' && (
                <span>🚨 <strong>No Authentication:</strong> No security checks. Use ONLY for attribution platforms (Adjust, AppsFlyer, Branch). Risk of fake conversions!</span>
              )}
            </div>
          </div>

          {formData.postbackAuthMethod === 'ip_whitelist' && (
            <div className="form-group full-width">
              <label htmlFor="whitelistIp">
                Whitelist IP Addresses*
                <span style={{ fontSize: '12px', color: '#a0aec0', marginLeft: '10px' }}>
                  (Required for IP Whitelist authentication)
                </span>
              </label>
              <textarea
                id="whitelistIp"
                name="whitelistIp"
                value={formData.whitelistIp}
                onChange={handleInputChange}
                className="form-textarea"
                rows={3}
                placeholder="Enter IP addresses separated by commas (e.g., 203.0.113.45, 198.51.100.22)"
                required={formData.postbackAuthMethod === 'ip_whitelist'}
              />
              <div style={{ marginTop: '8px', fontSize: '12px', color: '#a0aec0' }}>
                Only conversions from these IP addresses will be accepted. Separate multiple IPs with commas.
              </div>
            </div>
          )}

          {formData.postbackAuthMethod === 'hmac' && (
            <div className="form-group full-width">
              <label htmlFor="whitelistIp">
                Whitelist IP Addresses (Optional)
                <span style={{ fontSize: '12px', color: '#a0aec0', marginLeft: '10px' }}>
                  (Additional security layer for HMAC)
                </span>
              </label>
              <textarea
                id="whitelistIp"
                name="whitelistIp"
                value={formData.whitelistIp}
                onChange={handleInputChange}
                className="form-textarea"
                rows={3}
                placeholder="Enter IP addresses separated by commas (optional - adds extra security)"
              />
            </div>
          )}

          {error && (
            <div className="api-error-banner" role="alert">
              <strong>Error:</strong> {error}
            </div>
          )}

          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={handleCancel} disabled={loading}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Creating...' : 'Add Advertiser'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddAdvertiser;
